import { useCallback, useMemo } from 'react';
import { useEdges, useFlowView, useGetNodes, useNodes } from '@/components/Recipe/FlowContext';
import {
  getIteratorRunModeTargets,
  getIteratorRunModeTargetsTotalRuns,
  mergeIteratorRunModeTargetsByNodeId,
} from '@/utils/iterator-run-mode.utils';
import {
  DEFAULT_ITERATOR_RUN_MODE,
  getExplicitIteratorRunMode,
  getIteratorValues,
  resolveIteratorRunMode,
} from '@/utils/iterator.utils';
import { useWorkflowStore } from '@/state/workflow.state';
import {
  EMPTY_ITERATOR_RUN_MODES_BY_TARGET_NODE_ID,
  useIteratorRunConfigStore,
} from '@/state/iterator-run-config.state';
import type { IteratorRunMode, Node } from '@/types/node';

type UseIterationModeStateParams = {
  enabled?: boolean;
  nodeIds: string[];
};

function resolveTargetNodesRunMode(
  targetNodes: Node[],
  storedRunModesByTargetNodeId: Record<string, IteratorRunMode | undefined>,
) {
  return resolveIteratorRunMode(
    targetNodes.map(
      (targetNode) =>
        storedRunModesByTargetNodeId[targetNode.id] ?? getExplicitIteratorRunMode(targetNode.data.iteratorInput),
    ),
  );
}

export function useIterationModeState({ enabled = true, nodeIds }: UseIterationModeStateParams) {
  const getNodes = useGetNodes();
  const nodes = useNodes();
  const edges = useEdges();
  const flowView = useFlowView();
  const workflowId = useWorkflowStore((state) => state.recipe.id ?? '');
  const storedRunModesByTargetNodeId = useIteratorRunConfigStore(
    (state) => state.runModesByWorkflowId[workflowId] ?? EMPTY_ITERATOR_RUN_MODES_BY_TARGET_NODE_ID,
  );
  const setRunModeForTargets = useIteratorRunConfigStore((state) => state.setRunModeForTargets);

  const batchNodeIds = useMemo(() => new Set(nodeIds), [nodeIds]);

  const runModeTargets = useMemo(
    () =>
      enabled
        ? mergeIteratorRunModeTargetsByNodeId(
            nodeIds.flatMap((nodeId) => getIteratorRunModeTargets(nodeId, nodes, edges, batchNodeIds)),
          )
        : [],
    [batchNodeIds, edges, enabled, nodeIds, nodes],
  );

  const runModeTargetNodeIds = useMemo(() => runModeTargets.map((target) => target.nodeId), [runModeTargets]);

  const targetNodes = useMemo(
    () => nodes.filter((node) => runModeTargetNodeIds.includes(node.id)),
    [nodes, runModeTargetNodeIds],
  );

  const { runMode } = useMemo(
    () => resolveTargetNodesRunMode(targetNodes, storedRunModesByTargetNodeId),
    [storedRunModesByTargetNodeId, targetNodes],
  );

  const totalRuns = useMemo(
    () =>
      getIteratorRunModeTargetsTotalRuns(
        runModeTargets,
        runMode,
        (iterator) =>
          getIteratorValues(iterator.iteratorNode.id, iterator.iteratorNode.data, (nodeId) =>
            flowView.listSelectorOptions(nodeId),
          ).length,
        edges,
      ),
    [edges, flowView, runMode, runModeTargets],
  );

  const getLatestRunMode = useCallback(() => {
    if (!enabled) {
      return DEFAULT_ITERATOR_RUN_MODE;
    }
    const latestTargetNodes = getNodes().filter((node) => runModeTargetNodeIds.includes(node.id));
    const latestStoredRunModesByTargetNodeId =
      useIteratorRunConfigStore.getState().runModesByWorkflowId[workflowId] ??
      EMPTY_ITERATOR_RUN_MODES_BY_TARGET_NODE_ID;
    return resolveTargetNodesRunMode(latestTargetNodes, latestStoredRunModesByTargetNodeId).runMode;
  }, [enabled, getNodes, runModeTargetNodeIds, workflowId]);

  const handleRunModeChange = useCallback(
    (nextRunMode: IteratorRunMode) => {
      if (!enabled || !workflowId || !runModeTargetNodeIds.length) {
        return;
      }
      setRunModeForTargets(workflowId, runModeTargetNodeIds, nextRunMode);
    },
    [enabled, runModeTargetNodeIds, setRunModeForTargets, workflowId],
  );

  return {
    canSelectIterationMode: runModeTargetNodeIds.length > 0,
    getLatestRunMode,
    handleRunModeChange,
    runMode,
    totalRuns,
  };
}
